import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useLang } from "../i18n";
import { track } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-[#171717]/20 bg-[#F4F0E8] px-4 py-3.5 text-sm text-[#171717] placeholder:text-[#171717]/40 transition-colors focus:border-[#C6A46A] focus:outline-none rounded-[3px]";
const labelCls = "mb-2 block text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[#171717]/60";

const ContactForm = () => {
  const { lang, t } = useLang();
  const f = t.contactPage.form;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferredContact: "phone",
    service: "other",
    consultationType: "in-person",
    preferredDate: "",
    preferredTime: "",
    message: "",
    consent: false,
    company: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, lang });
      track("contact_form_submit", { service: form.service, type: form.consultationType });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "sensitive_data" ? f.sensitiveError : f.errorCopy);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[#C6A46A]/50 bg-[#EEE9DF] p-10 text-center" data-testid="form-success">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#C6A46A]">
          <Check className="h-5 w-5 text-[#C6A46A]" />
        </span>
        <h3 className="mt-6 text-2xl font-light tracking-tight text-[#171717]">{f.successTitle}</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#171717]/65">{f.successCopy}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contact-form" noValidate={false}>
      {/* honeypot — hidden from humans, irresistible to bots */}
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={set("company")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className={labelCls}>{f.name} *</label>
          <input id="cf-name" data-testid="field-name" required minLength={2} maxLength={120} className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" />
        </div>
        <div>
          <label htmlFor="cf-phone" className={labelCls}>{f.phone} *</label>
          <input id="cf-phone" data-testid="field-phone" required minLength={7} maxLength={30} type="tel" inputMode="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
      </div>

      <div>
        <label htmlFor="cf-email" className={labelCls}>{f.email} *</label>
        <input id="cf-email" data-testid="field-email" required type="email" inputMode="email" maxLength={160} className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-preferred" className={labelCls}>{f.preferredContact}</label>
          <select id="cf-preferred" data-testid="field-preferred-contact" className={inputCls} value={form.preferredContact} onChange={set("preferredContact")}>
            <option value="phone">{f.contactOptions.phone}</option>
            <option value="email">{f.contactOptions.email}</option>
            <option value="text">{f.contactOptions.text}</option>
          </select>
        </div>
        <div>
          <label htmlFor="cf-service" className={labelCls}>{f.service}</label>
          <select id="cf-service" data-testid="field-service" className={inputCls} value={form.service} onChange={set("service")}>
            {Object.entries(f.services).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-3">
        <div>
          <label htmlFor="cf-type" className={labelCls}>{f.type}</label>
          <select id="cf-type" data-testid="field-type" className={inputCls} value={form.consultationType} onChange={set("consultationType")}>
            <option value="in-person">{f.typeOptions["in-person"]}</option>
            <option value="virtual">{f.typeOptions.virtual}</option>
          </select>
        </div>
        <div>
          <label htmlFor="cf-date" className={labelCls}>{f.date}</label>
          <input id="cf-date" data-testid="field-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.preferredDate} onChange={set("preferredDate")} />
        </div>
        <div>
          <label htmlFor="cf-time" className={labelCls}>{f.time}</label>
          <input id="cf-time" data-testid="field-time" type="time" className={`${inputCls} [color-scheme:dark]`} value={form.preferredTime} onChange={set("preferredTime")} />
        </div>
      </div>

      <div>
        <label htmlFor="cf-message" className={labelCls}>{f.message}</label>
        <textarea
          id="cf-message"
          data-testid="field-message"
          rows={5}
          maxLength={2000}
          className={`${inputCls} resize-y`}
          placeholder={f.messagePlaceholder}
          value={form.message}
          onChange={set("message")}
        />
      </div>

      <label className="flex cursor-pointer items-start gap-3" data-testid="field-consent">
        <input
          type="checkbox"
          required
          checked={form.consent}
          onChange={set("consent")}
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer appearance-none border border-[#171717]/30 bg-transparent transition-colors checked:border-[#C6A46A] checked:bg-[#C6A46A] focus:outline-none focus:ring-1 focus:ring-[#C6A46A]"
        />
        <span className="text-xs leading-relaxed text-[#171717]/60">{f.consent}</span>
      </label>

      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit"
        className="w-full border border-[#C6A46A] bg-[#C6A46A] px-7 py-4 text-[0.72rem] font-medium uppercase tracking-[0.2em] text-[#171717] transition-colors duration-300 hover:bg-transparent hover:text-[#C6A46A] disabled:cursor-wait disabled:opacity-60 sm:w-auto"
      >
        {sending ? f.sending : f.submit}
      </button>
    </form>
  );
};

export default ContactForm;
